/**
 * A plant's defect words: typed wrapper over `GET /v1/twin/defect-words` and the word's writes
 * (platform-core/internal/twin/defect_words.go, migration 0271; plan 2026-10-27_cwc-meeting-follow-ups, F2).
 *
 * The list holds every word the org books, binds or describes, one entry per word keyed as the vocabulary keys it
 * (`source_key`: lower case, spaces and hyphens folded to underscores), with its pieces over the last 365 plant days,
 * its binding (how the analysis reads it), what the plant says about it (details), its sub categories and its
 * reference photos. None of it moves the analysis: the details and sub categories are kept apart from the binding.
 *
 * Every member reads the list. The writes are an org admin's (or Sahas staff acting in the org): a member gets 403
 * insufficient_permission. A photo's bytes are read with `GET /v1/twin/images/{image_id}` (lib/api/images).
 */

import { api } from "@/lib/api-client";
import type { ImageDatapoint } from "./images";
import type { VocabScope } from "./twin-vocabulary";

/** The query key of the org's defect list: every write to a word invalidates it. */
export const DEFECT_WORDS_KEY = ["twin-defect-words"] as const;

/** How a defect is found (the server's `how_found_choices`, in order). */
export type DefectHowFound =
  | "by_eye"
  | "sonic"
  | "hardness"
  | "gauge"
  | "xray"
  | "pressure"
  | "dye_or_magnetic"
  | "other";

/** The stage of the route a word is booked at (the server's `stage_choices`, in order). */
export type DefectStage = "casting" | "heat_treat" | "cleaning" | "machining" | "inspection" | "bookkeeping";

/** A choice with its words for a person (how a defect is found, a stage). */
export interface DefectChoice {
  key: string;
  label: string;
}

/** How the analysis reads a word: its vocabulary binding, whatever its status (only an approved one folds it). */
export interface DefectWordBinding {
  scope: VocabScope;
  target?: string;
  status: "proposed" | "approved" | "rejected";
}

/** What the plant says about one of its words. */
export interface DefectDetails {
  /** The word as the plant books it. */
  source_name: string;
  meaning: string;
  /** Each once, in the order of the choices. */
  how_found: DefectHowFound[];
  how_found_note: string;
  /** An entity of the org's plant model where it is found; null for none. */
  where_found_entity: string | null;
  /** That entity's name; null when none is set or it is gone. */
  where_found_entity_name: string | null;
  where_found_note: string;
  who_books: string;
  stage: DefectStage | null;
  /** users.id of who last set them ("" for none), and their name or email. */
  updated_by: string;
  updated_by_name: string;
  created_at: string;
  updated_at: string;
}

/** One sub category of a word. A retired one is kept, never deleted. */
export interface DefectSubcategory {
  /** From its name when it was added (folded as a word's key); never changes after. */
  key: string;
  name: string;
  meaning: string;
  sort: number;
  retired: boolean;
  retired_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** One entry of the org's defect list. */
export interface DefectWord {
  source_key: string;
  /** The details' name, else the binding's, else the word as most often booked, else the key in words. */
  source_name: string;
  /** Pieces booked under the word over the window; null when the org's day roll-ups hold nothing in it. */
  pieces_365d: number | null;
  /** The org's own data books the word, at any time. */
  booked: boolean;
  /** The pack's taxonomy reads it as one of its defects, or it is one of the org's own defect types. */
  known: boolean;
  binding: DefectWordBinding | null;
  details: DefectDetails | null;
  /** Active ones first, by sort then key. */
  subcategories: DefectSubcategory[];
  /** The word's reference photos, newest first. */
  photo_ids: string[];
}

/** GET /v1/twin/defect-words. */
export interface DefectWordList {
  /** By pieces in the window, most first, then by key. */
  items: DefectWord[];
  /** The plant days the pieces are summed over, both included (YYYY-MM-DD). */
  window: { first_day: string; last_day: string };
  /** One of the list's reads reached its cap (booked words, bindings, details, sub categories or photos): a word, or a
   *  part of one, may be missing. */
  words_cut: boolean;
  how_found_choices: DefectChoice[];
  stage_choices: DefectChoice[];
}

/** A word's details as PUT sets them: the whole of them, a field left out is set empty. */
export interface DefectDetailsInput {
  /** The word as booked; it must fold to the word's key. Left out: the stored name, else the binding's, is kept. */
  source_name?: string;
  /** At most 2000 characters; line breaks allowed. */
  meaning?: string;
  how_found?: DefectHowFound[];
  /** At most 1000 characters. */
  how_found_note?: string;
  /** An entity id of this org; null or "" for none. */
  where_found_entity?: string | null;
  /** At most 1000 characters. */
  where_found_note?: string;
  /** One line, at most 200 characters. */
  who_books?: string;
  /** null or "" for none. */
  stage?: DefectStage | null;
}

/**
 * One sub category as PUT takes it: with `key`, one the word has, set to these values (a rename keeps the key); without
 * one, a new one keyed from its name, never created retired. A sub category the request leaves out is left as it is.
 */
export interface DefectSubcategoryInput {
  key?: string;
  /** One line, 1 to 200 characters. */
  name: string;
  /** At most 2000 characters. */
  meaning?: string;
  /** 0 to 9999. */
  sort?: number;
  /** true retires it; false keeps it, or makes it, active. */
  retired?: boolean;
}

/** A reference photo, as the reject photo route takes one (an image of at most 25 MiB). */
export interface DefectPhotoInput {
  file: Blob;
  compressed?: boolean;
  width?: number;
  height?: number;
  filename?: string;
}

/** A word's route: its key, path-escaped (a key may hold any letter, and a '%'). */
export function defectWordPath(sourceKey: string): string {
  return `/v1/twin/defect-words/${encodeURIComponent(sourceKey)}`;
}

export const twinDefectWords = {
  /** Every word the org books, binds or describes (any member). */
  list: () => api.get<DefectWordList>("/v1/twin/defect-words"),
  /** Sets a word's details as a whole (org admin). 400 invalid_defect_details names what is wrong; 409
   *  too_many_defect_words when the org already describes 2000 words and this is a new one. */
  setDetails: (sourceKey: string, input: DefectDetailsInput) =>
    api.put<{ source_key: string; details: DefectDetails }>(`${defectWordPath(sourceKey)}/details`, input),
  /** Adds, edits, retires or unretires sub categories (org admin); answers every sub category of the word. 409
   *  subcategory_taken when a new one's key is one the word has (unretire that one instead); 409 too_many_defect_words
   *  when the org already describes 2000 words and this is a new one. */
  setSubcategories: (sourceKey: string, subcategories: DefectSubcategoryInput[]) =>
    api.put<{ source_key: string; subcategories: DefectSubcategory[] }>(`${defectWordPath(sourceKey)}/subcategories`, {
      subcategories,
    }),
  /** Adds a reference photo (org admin). 409 too_many_photos past 20 on the word. */
  uploadPhoto: (sourceKey: string, input: DefectPhotoInput) => {
    const fd = new FormData();
    fd.append("file", input.file, input.filename ?? "photo");
    fd.append("compressed", input.compressed ? "true" : "false");
    if (input.width) fd.append("width", String(input.width));
    if (input.height) fd.append("height", String(input.height));
    return api.postForm<ImageDatapoint>(`${defectWordPath(sourceKey)}/photos`, fd);
  },
  /** Removes one of the word's reference photos (org admin); 404 photo_not_found for any other image. */
  deletePhoto: (sourceKey: string, imageId: string) =>
    api.delete<void>(`${defectWordPath(sourceKey)}/photos/${encodeURIComponent(imageId)}`),
};
